/**
 * Hook dữ liệu trang chủ: tin nổi bật và các section carousel duyệt tin.
 *
 * Tải toàn bộ tin + danh mục, rồi ghép section bán / phổ biến (theo lượt xem)
 * với giới hạn số item mỗi section.
 */
import { useEffect, useMemo, useState } from 'react';
import { categoryApi } from '../../categories/api/categoryApi';
import { propertyApi } from '../api/propertyApi';
import { buildBrowseSectionPath } from '../../../shared/utils/browseSectionSearch';
import { TRANSACTION_TYPES } from '../../../shared/constants/property';
import { TEXT } from '../../../shared/languages';
import { waitMinimumDelay } from '../../../shared/utils/waitMinimumDelay';

const MAX_ITEMS_PER_SECTION = 12;

/**
 * Hook trang chủ: featured properties, danh mục và section listing carousel.
 *
 * @returns {Object} featuredProperties, categoryList, listingSections, isLoadingHomeData
 */
export const useFeaturedProperties = () => {
  const [allProperties, setAllProperties] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [isLoadingHomeData, setIsLoadingHomeData] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchStartedAt = Date.now();

    setIsLoadingHomeData(true);

    Promise.all([propertyApi.getProperties(), categoryApi.getCategories()])
      .then(([propertyResponse, categoryResponse]) => {
        if (!isMounted) {
          return;
        }

        setAllProperties(propertyResponse.data.data || []);
        setCategoryList(categoryResponse.data.data || []);
      })
      .finally(() => {
        waitMinimumDelay(fetchStartedAt).then(() => {
          if (isMounted) {
            setIsLoadingHomeData(false);
          }
        });
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Gom tin thành các section carousel (bán, xem nhiều) với link "xem thêm"
  const listingSections = useMemo(() => {
    const t = TEXT.home;
    const sections = [];

    const saleProperties = allProperties
      .filter((property) => property.transaction_type === TRANSACTION_TYPES.sale)
      .slice(0, MAX_ITEMS_PER_SECTION);

    if (saleProperties.length) {
      sections.push({
        id: 'sale',
        categoryId: null,
        title: t.sectionSale,
        href: buildBrowseSectionPath('sale'),
        properties: saleProperties,
      });
    }

    const popularProperties = [...allProperties]
      .filter((property) => property.transaction_type === TRANSACTION_TYPES.sale)
      .sort((left, right) => (right.views_count || 0) - (left.views_count || 0))
      .slice(0, MAX_ITEMS_PER_SECTION);

    if (popularProperties.length) {
      sections.push({
        id: 'popular',
        categoryId: null,
        title: t.sectionPopular,
        href: buildBrowseSectionPath('popular'),
        properties: popularProperties,
      });
    }

    return sections;
  }, [allProperties]);

  return {
    featuredProperties: allProperties.slice(0, 6),
    categoryList,
    listingSections,
    isLoadingHomeData,
  };
};
